import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { PhotoFileInput } from "@/components/ui/photo-file-input"
import { PhotoLibraryDialog } from "@/components/photo-library/PhotoLibraryDialog"
import { StatusBadge } from "@/components/studio/StatusBadge"
import { formatCost } from "@/components/studio/types"
import {
  ASPECT_LABELS,
  MODEL_LABELS,
  PURPOSE_LABELS,
  type StudioImageJson,
} from "@/components/image-studio/types"
import type { LibraryPhoto } from "@/components/pages-cms/types"
import type { PageProps } from "@/types/inertia"

type Props = PageProps & {
  images: StudioImageJson[]
  capabilities: { ai_enabled: boolean; media_enabled: boolean }
  purposes: string[]
  aspect_ratios: string[]
  model_overrides: string[]
  photo_library: LibraryPhoto[]
}

const BUSY_STATUSES = new Set(["queued", "generating"])

function CapabilityBanner({ capabilities }: { capabilities: Props["capabilities"] }) {
  if (capabilities.media_enabled && capabilities.ai_enabled) return null
  return (
    <div className="mt-4 rounded-lg border border-hairline bg-surface px-4 py-3">
      <p className="text-sm text-ink-muted">
        {!capabilities.media_enabled &&
          "Set FAL_KEY to enable image generation. "}
        {capabilities.media_enabled && !capabilities.ai_enabled &&
          "Prompts are sent to the image model exactly as written — set ANTHROPIC_API_KEY and Claude will polish them first."}
      </p>
    </div>
  )
}

export default function Index() {
  const { props } = usePage<Props>()
  const { images, capabilities } = props

  const [description, setDescription] = React.useState("")
  const [purpose, setPurpose] = React.useState("photo")
  const [aspectRatio, setAspectRatio] = React.useState("1:1")
  const [variations, setVariations] = React.useState(2)
  const [style, setStyle] = React.useState("")
  const [brand, setBrand] = React.useState("gisela")
  const [modelOverride, setModelOverride] = React.useState("auto")
  const [prompt, setPrompt] = React.useState<string | null>(null)
  const [uploads, setUploads] = React.useState<File[]>([])
  const [libraryRefs, setLibraryRefs] = React.useState<LibraryPhoto[]>([])
  const [libraryOpen, setLibraryOpen] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const formRef = React.useRef<HTMLDivElement>(null)

  const uploadPreviews = React.useMemo(
    () => uploads.map((file) => URL.createObjectURL(file)),
    [uploads],
  )
  React.useEffect(
    () => () => uploadPreviews.forEach((url) => URL.revokeObjectURL(url)),
    [uploadPreviews],
  )

  // Batches keep the newest-first order the server sends.
  const batches = React.useMemo(() => {
    const map = new Map<string, StudioImageJson[]>()
    for (const image of images) {
      const list = map.get(image.batch_id) ?? []
      list.push(image)
      map.set(image.batch_id, list)
    }
    return [...map.values()]
  }, [images])

  // Poll with an Inertia partial reload while anything is generating — no
  // JSON endpoints, per the Inertia response rules.
  const busy = images.some((image) => BUSY_STATUSES.has(image.status))
  React.useEffect(() => {
    if (!busy) return
    const interval = window.setInterval(() => {
      router.reload({ only: ["images"] })
    }, 5000)
    return () => window.clearInterval(interval)
  }, [busy])

  function resetForm() {
    setDescription("")
    setStyle("")
    setPrompt(null)
    setUploads([])
    setLibraryRefs([])
    setModelOverride("auto")
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!description.trim() || submitting) return
    router.post(
      "/admin/image-studio/images",
      {
        description,
        purpose,
        aspect_ratio: aspectRatio,
        variations,
        style,
        brand,
        ...(prompt ? { prompt } : {}),
        ...(modelOverride !== "auto" ? { provider_model: modelOverride } : {}),
        ...(uploads.length > 0 ? { reference_images: uploads } : {}),
        reference_photo_ids: libraryRefs.map((photo) => photo.id),
      },
      {
        forceFormData: uploads.length > 0,
        preserveScroll: true,
        onStart: () => setSubmitting(true),
        onFinish: () => setSubmitting(false),
        onSuccess: resetForm,
      },
    )
  }

  function rerun(image: StudioImageJson) {
    setDescription(image.description)
    setPurpose(image.purpose)
    setAspectRatio(image.aspect_ratio)
    setStyle(image.style ?? "")
    setBrand(image.brand)
    setPrompt(image.prompt)
    setVariations(1)
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const referenceCount = uploads.length + libraryRefs.length

  return (
    <AdminShell>
      <Head title="Image Studio">
        <meta
          name="description"
          content="Generate AI images for the websites and save the winners into the Photo library."
        />
        <meta property="og:title" content="Image Studio" />
        <meta
          property="og:description"
          content="Generate AI images for the websites and save the winners into the Photo library."
        />
      </Head>

      <PageHeader
        title="Image Studio"
        description="Describe the image you need — with reference photos if a real room or person should appear — and save the winners into Photos."
      />
      <CapabilityBanner capabilities={capabilities} />

      <div ref={formRef} className="mt-6 rounded-lg border border-hairline bg-surface">
        <form onSubmit={submit} className="space-y-4 p-5">
          <div className="space-y-2">
            <label htmlFor="studio-description">What do you want?</label>
            <Textarea
              id="studio-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Gisela welcoming a client in the blue therapy room, warm afternoon light…"
            />
          </div>

          {prompt !== null && (
            <div className="space-y-2">
              <label htmlFor="studio-prompt">Exact prompt (sent as written, no AI polishing)</label>
              <Textarea
                id="studio-prompt"
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => setPrompt(null)}>
                Discard exact prompt — describe freshly instead
              </Button>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <label htmlFor="studio-purpose">Kind of image</label>
              <Select
                id="studio-purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
              >
                {props.purposes.map((p) => (
                  <option key={p} value={p}>
                    {PURPOSE_LABELS[p] ?? p}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="studio-aspect">Shape</label>
              <Select
                id="studio-aspect"
                value={aspectRatio}
                onChange={(e) => setAspectRatio(e.target.value)}
              >
                {props.aspect_ratios.map((ratio) => (
                  <option key={ratio} value={ratio}>
                    {ASPECT_LABELS[ratio] ?? ratio}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="studio-variations">Variations</label>
              <Select
                id="studio-variations"
                value={String(variations)}
                onChange={(e) => setVariations(Number(e.target.value))}
              >
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="studio-brand">For</label>
              <Select id="studio-brand" value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option value="gisela">Gisela's site</option>
                <option value="sanctuary">Sanctuary</option>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="studio-style">Style hints (optional)</label>
            <Input
              id="studio-style"
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              placeholder="e.g. warm, premium, natural light"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="studio-references">Reference photos (optional)</label>
            <p className="text-xs text-ink-muted">
              Add photos of a real room or person to keep in the image — references switch
              generation to the edit model.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <PhotoFileInput
                id="studio-references"
                multiple
                variant="secondary"
                size="sm"
                onSelect={(files) => setUploads((prev) => [...prev, ...files])}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setLibraryOpen(true)}
              >
                Choose from library
              </Button>
            </div>
            {referenceCount > 0 && (
              <ul className="flex flex-wrap gap-2">
                {uploads.map((file, index) => (
                  <li key={`upload-${index}`} className="relative">
                    <img
                      src={uploadPreviews[index]}
                      alt={file.name}
                      className="h-16 w-16 rounded-md border border-hairline object-cover"
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => setUploads((prev) => prev.filter((_, i) => i !== index))}
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-hairline bg-surface text-xs text-ink-muted hover:text-ink-body"
                    >
                      ×
                    </button>
                  </li>
                ))}
                {libraryRefs.map((photo) => (
                  <li key={`library-${photo.id}`} className="relative">
                    {photo.url ? (
                      <img
                        src={photo.url}
                        alt={photo.alt_text_en ?? ""}
                        className="h-16 w-16 rounded-md border border-hairline object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-md border border-hairline bg-page text-xs text-ink-muted">
                        Photo
                      </div>
                    )}
                    <button
                      type="button"
                      aria-label="Remove library photo"
                      onClick={() =>
                        setLibraryRefs((prev) => prev.filter((p) => p.id !== photo.id))
                      }
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-hairline bg-surface text-xs text-ink-muted hover:text-ink-body"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <details>
            <summary className="cursor-pointer text-sm text-ink-muted">Advanced</summary>
            <div className="mt-3 max-w-sm space-y-2">
              <label htmlFor="studio-model">Model</label>
              <Select
                id="studio-model"
                value={modelOverride}
                onChange={(e) => setModelOverride(e.target.value)}
              >
                <option value="auto">Automatic (recommended)</option>
                {props.model_overrides.map((model) => (
                  <option key={model} value={model}>
                    {MODEL_LABELS[model] ?? model}
                  </option>
                ))}
              </Select>
            </div>
          </details>

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={!capabilities.media_enabled || !description.trim() || submitting}
            >
              {submitting ? "Sending…" : `Generate ${variations} ${variations === 1 ? "image" : "images"}`}
            </Button>
            <p className="text-xs text-ink-muted">A few pence per image, ready in under a minute.</p>
          </div>
        </form>
      </div>

      <PhotoLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        library={props.photo_library}
        onSelect={(photo) =>
          setLibraryRefs((prev) => (prev.some((p) => p.id === photo.id) ? prev : [...prev, photo]))
        }
      />

      <div className="mt-8 space-y-8">
        {batches.length === 0 && (
          <p className="text-ink-muted">Nothing generated yet — your images will appear here.</p>
        )}
        {batches.map((batch) => (
          <section key={batch[0].batch_id}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline pb-2">
              <p className="font-medium text-ink-display">{batch[0].description}</p>
              <p className="text-xs text-ink-muted">
                {PURPOSE_LABELS[batch[0].purpose] ?? batch[0].purpose} ·{" "}
                {new Date(batch[0].created_at).toLocaleString()}
              </p>
            </div>
            {batch[0].prompt && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-ink-muted">Prompt used</summary>
                <p className="mt-1 text-sm text-ink-body">{batch[0].prompt}</p>
              </details>
            )}
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {batch.map((image) => (
                <div
                  key={image.id}
                  className="overflow-hidden rounded-lg border border-hairline bg-surface"
                >
                  <div
                    className="flex w-full items-center justify-center bg-page"
                    style={{ aspectRatio: image.aspect_ratio.replace(":", " / ") }}
                  >
                    {image.image_url ? (
                      <img
                        src={image.image_url}
                        alt={image.description}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <StatusBadge status={image.status} />
                    )}
                  </div>
                  <div className="space-y-3 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={image.status} />
                      <span className="text-xs text-ink-muted">
                        {formatCost(image.cost_estimate_cents)}
                        {image.reference_count > 0 &&
                          ` · ${image.reference_count} reference${image.reference_count === 1 ? "" : "s"}`}
                      </span>
                    </div>
                    {image.status === "failed" && image.error_message && (
                      <p className="text-sm text-danger">{image.error_message}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {image.status === "ready" && !image.in_library && (
                        <Button
                          size="sm"
                          onClick={() =>
                            router.patch(
                              `/admin/image-studio/images/${image.id}/save_to_photos`,
                              {},
                              { preserveScroll: true },
                            )
                          }
                        >
                          Save to Photos
                        </Button>
                      )}
                      {image.in_library && (
                        <span className="inline-flex items-center text-xs text-ink-muted">
                          In the Photo library ✓
                        </span>
                      )}
                      <Button size="sm" variant="secondary" onClick={() => rerun(image)}>
                        Re-run
                      </Button>
                      {image.download_url && (
                        <Button size="sm" variant="secondary" asChild>
                          <a href={image.download_url} download>
                            Download
                          </a>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          router.delete(`/admin/image-studio/images/${image.id}`, {
                            preserveScroll: true,
                          })
                        }
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </AdminShell>
  )
}
